import { v4 as nanoid } from 'uuid'

import { ShippableEventKind, VersionedEvent } from '../'
import { Bet, Player } from '../../model'
import { Snapshot, TableSnapshotter } from '../../model/Snapshot'
import { Table } from '../../model/Table'

export enum TableEventKind {
  Created = 'poker:table:created',
  Started = 'poker:table:started',
  Sleep = 'poker:table:sleep',
  Paused = 'poker:table:paused',
  Resumed = 'poker:table:resumed',
  Reset = 'poker:table:reset',
  Finished = 'poker:table:finished',
  TimerStarted = 'poker:table:timer_started',

  StreetFinished = 'poker:table:street:finished',

  HandNew = 'poker:table:hand:new',
  HandCurrentPlayer = 'poker:table:hand:current_player',
  HandDealtCards = 'poker:table:hand:dealt',
  HandAllPlayersAllIn = 'poker:table:hand:all_players_allin',
  HandEveryoneFolded = 'poker:table:hand:everyone_folded',
  HandShowdown = 'poker:table:hand:showdown',
  HandWinners = 'poker:table:hand:winners',
  HighestBetPlayer = 'poker:table:highest:bet_player',

  PlayerJoined = 'poker:table:player:joined',
  PlayerWaitlisted = 'poker:table:player:waitlisted',
  PlayerObserver = 'poker:table:player:observer',
  PlayerOut = 'poker:table:player:out',
  PlayerForeverOut = 'poker:table:player:foreverout',
  PlayerLeft = 'poker:table:player:left',
  PlayerPostedBlind = 'poker:table:player:post_blind',
  Disconnected = 'poker:table:player:disconnected',

  PlayerBet = 'poker:table:player:bet',
  PlayerAllIn = 'poker:table:player:allin',
  PlayerChecked = 'poker:table:player:checked',
  PlayerCalled = 'poker:table:player:called',
  PlayerFolded = 'poker:table:player:folded',
  PlayerReset = 'poker:table:player:reset',
  PlayerOutOfChips = 'poker:table:player:outofchips',
  PlayerBuyIn = 'poker:table:player:buyin',
  PlayerAddChips = 'poker:table:player:addChips',
  BuyInForAllPlayers = 'poker:table::buyInForAllPlayers',
  PlayerSitOut = 'poker:table:player:sitout',
  PlayerEndOfTurn = 'poker:table:player:endofturn',
  DoNoActionForPlayerEvent = 'poker:table:player:doNoActionForPlayerEvent',
  ShowCardsForTeacherEvent = 'poker:table:player:showCardsForTeacherEvent',
  ShowCardsForEveryoneEvent = 'poker:table:player:showCardsForEveryoneEvent',
  HideCards = 'poker:table:player:hideCards',
  HidePlayerCards = 'poker:table:player:hidePlayerCards',
  ShowPlayerCardsForTeacherEvent = 'poker:table:player:showPlayerCardsForTeacherEvent',
  ShowPlayerCardsForEveryoneEvent = 'poker:table:player:showPlayerCardsForEveryoneEvent',
  CaptureLastAction = 'poker:table:player:captureLastAction',
  AutoStart = 'poker:table:auto:start',
  RankedHandEvent = 'poker:table:player:rankedHandEvent',
  CalculateNextBlindEvent = 'poker:table:calculate:nextBlindEvent',
  TournamentBlindEvent = 'poker:table:tournament:BlindEvent',
  PlayerRemovedByTeacherEvent = 'poker:table:player:removedByTeacherEvent',
  SetPlayerBuyInEvent = 'poker:table:player:setBuyInEvent',
  CardsVisibleOnShowDownEvent = 'poker:table:player:cardsVisibleOnShowDownEvent',
  FetchLastGameState = 'poker:table:fetch_game_state',
  BotsManuallyAdded = 'poker:table:botsManuallyAdded'
}

export const eventsThatTriggerSleep: ShippableEventKind[] = [
  TableEventKind.StreetFinished,
  TableEventKind.HandShowdown,
  TableEventKind.HandWinners
]

export interface TableEvent extends VersionedEvent {
  id: string
  timestamp: number
  kind: TableEventKind
  snapshot: Snapshot
}

export class TableEvents {
  static tableCreatedEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.Created, table)
  }

  static tableStartedEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.Started, table, table.getCurrentPlayer())
  }

  static tableResumedEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.Resumed, table, table.getCurrentPlayer())
  }

  static tablePausedEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.Paused, table)
  }

  static tableSleepEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.Sleep, table)
  }

  static tableResetEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.Reset, table)
  }

  static tableFinishedEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.Finished, table)
  }

  static streetFinishedEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.StreetFinished, table)
  }

  static handNewEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.HandNew, table)
  }

  static handCurrentPlayerEvent(table: Table, player: Player) {
    return TableEvents.createEvent(TableEventKind.HandCurrentPlayer, table, player)
  }

  static higestBetPlayerEvent(table: Table, player: Player) {
    return TableEvents.createEvent(TableEventKind.HighestBetPlayer, table, player)
  }

  static handDealtCardsEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.HandDealtCards, table)
  }

  static tableTimerStartedEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.TimerStarted, table)
  }

  static handShowdownEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.HandShowdown, table)
  }

  static handAllPlayersAllIn(table: Table) {
    return TableEvents.createEvent(TableEventKind.HandAllPlayersAllIn, table)
  }

  static handEveryoneFolded(table: Table) {
    return TableEvents.createEvent(TableEventKind.HandEveryoneFolded, table)
  }

  static handWinnersEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.HandWinners, table)
  }

  static playerEndOfTurn(table: Table) {
    return TableEvents.createEvent(TableEventKind.PlayerEndOfTurn, table, table.getCurrentPlayer())
  }

  static playerJoinedEvent(table: Table, player: Player) {
    return TableEvents.createEvent(TableEventKind.PlayerJoined, table, player)
  }

  static botsManuallyAddedEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.BotsManuallyAdded, table)
  }

  static playerWaitlistedEvent(table: Table, player: Player) {
    return TableEvents.createEvent(TableEventKind.PlayerWaitlisted, table, player)
  }

  static playerObserverEvent(table: Table, player: Player) {
    return TableEvents.createEvent(TableEventKind.PlayerObserver, table, player)
  }

  static playerForeverOutEvent(table: Table, player: Player) {
    return TableEvents.createEvent(TableEventKind.PlayerForeverOut, table, player)
  }

  static playerOutEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.PlayerOut, table)
  }

  static playerLeftEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.PlayerLeft, table)
  }

  static playerPostedBlind(table: Table, player?: Player, bet?: Bet) {
    return TableEvents.createEvent(TableEventKind.PlayerPostedBlind, table, player, bet)
  }

  static playerBetEvent(table: Table, player?: Player, bet?: Bet) {
    return TableEvents.createEvent(TableEventKind.PlayerBet, table, player, bet)
  }

  static playerAllInEvent(table: Table, player?: Player) {
    return TableEvents.createEvent(TableEventKind.PlayerAllIn, table, player)
  }

  static playerCheckedEvent(table: Table, player?: Player) {
    return TableEvents.createEvent(TableEventKind.PlayerChecked, table, player)
  }

  static playerCalledEvent(table: Table, player: Player) {
    return TableEvents.createEvent(TableEventKind.PlayerCalled, table, player)
  }

  static playerFoldedEvent(table: Table, player: Player) {
    return TableEvents.createEvent(TableEventKind.PlayerFolded, table, player)
  }

  static playerResetEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.PlayerReset, table)
  }

  static playerOutOfChipsEvent(table: Table, player: Player) {
    return TableEvents.createEvent(TableEventKind.PlayerOutOfChips, table, player)
  }

  static playerBuyInEvent(table: Table, player: Player) {
    return TableEvents.createEvent(TableEventKind.PlayerBuyIn, table, player)
  }

  static playerAddChipsEvent(table: Table, player: Player) {
    return TableEvents.createEvent(TableEventKind.PlayerAddChips, table, player)
  }

  static buyInForAllPlayers(table: Table) {
    return TableEvents.createEvent(TableEventKind.BuyInForAllPlayers, table)
  }

  static playerSitOutEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.PlayerSitOut, table)
  }

  static disconnected(table: Table, player: Player) {
    return TableEvents.createEvent(TableEventKind.Disconnected, table, player)
  }

  static doNoActionForPlayerEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.DoNoActionForPlayerEvent, table)
  }

  static showCardsForTeacherEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.ShowCardsForTeacherEvent, table)
  }

  static showCardsForEveryoneEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.ShowCardsForEveryoneEvent, table)
  }

  static hideCardsEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.HideCards, table)
  }

  static hidePlayerCardsEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.HidePlayerCards, table)
  }

  static showPlayerCardsForTeacherEvent(table: Table, player: Player) {
    return TableEvents.createEvent(TableEventKind.ShowPlayerCardsForTeacherEvent, table, player)
  }

  static showPlayerCardsForEveryoneEvent(table: Table, player: Player) {
    return TableEvents.createEvent(TableEventKind.ShowPlayerCardsForEveryoneEvent, table, player)
  }

  static captureLastAction(table: Table) {
    return TableEvents.createEvent(TableEventKind.CaptureLastAction, table)
  }

  static autoStartEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.AutoStart, table)
  }

  static rankedHandEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.RankedHandEvent, table)
  }

  static calculateNextBlindEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.CalculateNextBlindEvent, table)
  }

  static tournamentBlindEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.TournamentBlindEvent, table)
  }

  static playerRemovedByTeacherEvent(table: Table, player: Player) {
    return TableEvents.createEvent(TableEventKind.PlayerRemovedByTeacherEvent, table, player)
  }

  static setPlayerBuyInEvent(table: Table, player: Player) {
    return TableEvents.createEvent(TableEventKind.SetPlayerBuyInEvent, table, player)
  }

  static cardsVisibleOnShowDownEvent(table: Table) {
    return TableEvents.createEvent(TableEventKind.CardsVisibleOnShowDownEvent, table)
  }

  static createEvent(eventKind: TableEventKind, table: Table, player?: Player, bet?: Bet) {
    let snap: Snapshot
    if (player && bet) {
      snap = TableSnapshotter.forPlayerWithBet(table, player.getId(), bet)
    } else if (player) {
      snap = TableSnapshotter.forPlayer(table, player.getId())
    } else {
      snap = TableSnapshotter.forTable(table)
    }

    return <TableEvent>{
      id: nanoid(),
      name: eventKind,
      timestamp: Date.now(),
      kind: eventKind,
      snapshot: snap
    }
  }
}
